// Avoid re-exports in favor of direct imports or entry-point files
/* eslint-disable no-barrel-files/no-barrel-files */

import { useCallback } from 'react';
import { defineMessages } from 'react-intl';
import type { GraphQLTaggedNode } from 'react-relay';
import { graphql } from 'react-relay';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloError } from '@confluence/apollo-relay-interop/entry-points/apolloTypes.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { expVal } from '@confluence/feature-experiments';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useWriteFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useWriteFragmentInterop.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import type { useSpaceViewsOptionsSpaceViewsPersistenceWriteFragment$data } from './__generated__/useSpaceViewsOptionsSpaceViewsPersistenceWriteFragment.graphql.ts';
import useSpaceViewsOptionsSpaceViewsPersistenceWriteFragment_artifact from './__generated__/useSpaceViewsOptionsSpaceViewsPersistenceWriteFragment.graphql.ts';
import type {
	useSpaceViewsOptionsSpaceViewsPersistenceMutation$data,
	useSpaceViewsOptionsSpaceViewsPersistenceMutation$variables,
} from './__generated__/useSpaceViewsOptionsSpaceViewsPersistenceMutation.graphql.ts';
import type {
	useSpaceViewsOptionsPersistenceQuery$data,
	useSpaceViewsOptionsPersistenceQuery$variables,
	SpaceViewsPersistenceOption,
} from './__generated__/useSpaceViewsOptionsPersistenceQuery.graphql.ts';
import type { useSpaceViewsOptionsPersistence_userPreferences$data } from './__generated__/useSpaceViewsOptionsPersistence_userPreferences.graphql.ts';

export const SpaceViewsOptionsPersistenceFragment: GraphQLTaggedNode = graphql`
	fragment useSpaceViewsOptionsPersistence_userPreferences on UserPreferences {
		spaceViewsPersistence(spaceKey: $spaceKey)
	}
`;

const useSpaceViewsOptionsSpaceViewsPersistenceMutation = graphql`
	mutation useSpaceViewsOptionsSpaceViewsPersistenceMutation(
		$spaceKey: String!
		$persistenceOption: SpaceViewsPersistenceOption!
	) {
		updateUserPreferences(
			userPreferences: {
				spaceViewsPersistence: { spaceKey: $spaceKey, persistenceOption: $persistenceOption }
			}
		) {
			spaceViewsPersistence(spaceKey: $spaceKey)
		}
	}
`;

const useSpaceViewsOptionsPersistenceQuery = graphql`
	query useSpaceViewsOptionsPersistenceQuery($spaceKey: String!) {
		userPreferences {
			...useSpaceViewsOptionsPersistence_userPreferences @relay(mask: false)
		}
	}
`;

export const sortI18n: {
	TREE: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	RECENTLY_VIEWED: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	RECENTLY_MODIFIED: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	TITLE_AZ: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	TREE: {
		id: 'space-views.tree',
		defaultMessage: 'Tree',
		description: 'dropdown menu value for the default tree view',
	},
	RECENTLY_VIEWED: {
		id: 'space-views.recently.viewed',
		defaultMessage: 'My visits',
		description:
			'dropdown menu value for filtering by only pages the current viewer has recently viewed',
	},
	RECENTLY_MODIFIED: {
		id: 'space-views.recently.modified',
		defaultMessage: 'Last updated',
		description:
			'dropdown menu value for sorting by pages in the order that they were most recently modified',
	},
	TITLE_AZ: {
		id: 'space-views.a-z.desc',
		defaultMessage: 'Title - A to Z',
		description: 'dropdown menu value for sorting by title from A-Z',
	},
});

export const useSpaceViewsOptions = (
	spaceKey: string | undefined,
	isLicensed = false,
	initialPersistenceData?: Pick<
		useSpaceViewsOptionsPersistence_userPreferences$data,
		'spaceViewsPersistence'
	>,
): {
	spaceViewsSort: SpaceViewsPersistenceOption;
	setSpaceViewsSort: (newSort: SpaceViewsPersistenceOption) => void;
	queryLoading: boolean;
	queryError: ApolloErrorInterop | undefined;
	mutationLoading: boolean;
	mutationError: ApolloError | undefined;
} => {
	const {
		data,
		loading: queryLoading,
		error: queryError,
	} = useQueryInterop<
		useSpaceViewsOptionsPersistenceQuery$data,
		useSpaceViewsOptionsPersistenceQuery$variables
	>(useSpaceViewsOptionsPersistenceQuery, {
		variables: { spaceKey: spaceKey ?? '' },
		skip:
			!spaceKey ||
			!isLicensed ||
			(initialPersistenceData !== undefined &&
				!StatsigConfigurations.isKillSwitchOn('confluence_space_nav_space_views_frag_reader') &&
				expVal('confluence_exp_space_nav_contract', 'isEnabled', false)),
	});

	const [updatePersistence, { loading: mutationLoading, error: mutationError }] =
		useMutationInterop<
			useSpaceViewsOptionsSpaceViewsPersistenceMutation$data,
			useSpaceViewsOptionsSpaceViewsPersistenceMutation$variables
		>(useSpaceViewsOptionsSpaceViewsPersistenceMutation);

	const writeFragmentInterop = useWriteFragmentInterop<
		Omit<useSpaceViewsOptionsSpaceViewsPersistenceWriteFragment$data, ' $fragmentType'>
	>({
		fragmentArtifact: useSpaceViewsOptionsSpaceViewsPersistenceWriteFragment_artifact,
		fragmentSpec: graphql`
			fragment useSpaceViewsOptionsSpaceViewsPersistenceWriteFragment on UserPreferences {
				spaceViewsPersistence(spaceKey: $spaceKey)
				__typename
			}
		`,
	});
	const setSpaceViewsSort = useCallback(
		(newSort: SpaceViewsPersistenceOption): void => {
			if (!spaceKey) {
				return;
			}

			updatePersistence({
				variables: {
					spaceKey,
					persistenceOption: newSort,
				},
			}).catch(() => {}); // ignoring errors here because we are consuming error from useMutation

			// Update the cache to keep UI updated regardless of whether the mutation succeeds
			writeFragmentInterop({
				fragmentId: `$ROOT_QUERY.userPreferences`,
				data: {
					spaceViewsPersistence: newSort,
					__typename: 'UserPreferences',
				},
				variables: { spaceKey },
			});
		},
		[spaceKey, updatePersistence, writeFragmentInterop],
	);

	const spaceViewsSort: SpaceViewsPersistenceOption =
		initialPersistenceData?.spaceViewsPersistence ||
		data?.userPreferences?.spaceViewsPersistence ||
		'TREE'; //tree is default

	return {
		spaceViewsSort,
		setSpaceViewsSort,
		queryLoading: initialPersistenceData ? false : queryLoading,
		queryError: initialPersistenceData ? undefined : queryError,
		mutationLoading,
		mutationError,
	};
};
